import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, MessageCircle } from "lucide-react";
import { WHATSAPP_NUMBER } from "@/lib/site-data";

const STORAGE_KEY = "xyro_exit_intent_seen";

export default function ExitIntentPopup() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "" });
  const [error, setError] = useState("");

  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    const onLeave = (e) => {
      if (e.clientY <= 0) {
        setOpen(true);
        sessionStorage.setItem(STORAGE_KEY, "1");
        document.removeEventListener("mouseleave", onLeave);
      }
    };
    const t = setTimeout(() => document.addEventListener("mouseleave", onLeave), 10000);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  const submit = (e) => {
    e.preventDefault();
    setError("");
    if (!form.full_name || !form.email) {
      setError("Please enter your name and email.");
      return;
    }
    const text = [
      "Hi Xyro Tech, I'd like to claim my FREE AI Resume Review.",
      "",
      `Name: ${form.full_name}`,
      `Email: ${form.email}`,
    ].join("\n");
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="exit-intent-popup"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
          onClick={() => setOpen(false)}
        >
          <motion.div
            initial={{ scale: 0.92, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.92, y: 20 }}
            transition={{ type: "spring", damping: 22, stiffness: 200 }}
            onClick={(e) => e.stopPropagation()}
            className="relative glass-prism w-full max-w-lg p-8 md:p-10"
          >
            <button
              data-testid="exit-popup-close"
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/[0.04] border border-white/[0.08] text-zinc-400 hover:text-white"
              aria-label="Close popup"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#2B4DFF]/15 border border-[#2B4DFF]/30 text-xs text-[#9db1ff] mb-5">
              <Sparkles className="w-3.5 h-3.5" />
              Free AI Resume Review
            </div>
            <h3 className="font-heading text-3xl md:text-4xl font-semibold text-white tracking-tight leading-[1.1]">
              Don&apos;t leave yet —<br />
              <span className="text-gradient-electric">get a free AI resume review.</span>
            </h3>
            <p className="mt-4 text-zinc-400 text-sm leading-relaxed">
              Discover exactly why your resume isn&apos;t getting interview calls in the US — and get a personalized action plan, on the house.
            </p>

            <form onSubmit={submit} className="mt-6 space-y-3" data-testid="exit-popup-form">
              <input
                type="text"
                placeholder="Full name"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                data-testid="exit-popup-name"
                className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#2B4DFF]/60 focus:bg-white/[0.06] transition"
              />
              <input
                type="email"
                placeholder="Email address"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="exit-popup-email"
                className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#2B4DFF]/60 focus:bg-white/[0.06] transition"
              />
              {error && <p className="text-sm text-red-400">{error}</p>}
              <button
                type="submit"
                data-testid="exit-popup-submit"
                className="btn-primary-xyro inline-flex w-full justify-center items-center gap-2 rounded-full px-6 py-3.5 font-medium"
              >
                <MessageCircle className="w-4 h-4" /> Claim On WhatsApp
              </button>
            </form>
            <p className="text-xs text-zinc-500 mt-3">
              No spam. We respect your privacy.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
